import { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface QRCodeDisplayProps {
  address: string;
  coinType: string;
  network: string;
}

const QRCodeDisplay = ({ address, coinType, network }: QRCodeDisplayProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      toast.success(t('common.address_copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy address');
    }
  };

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = 512;
    canvas.height = 512;

    img.onload = () => {
      ctx?.drawImage(img, 0, 0, 512, 512);
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${coinType}-deposit-qr.png`;
          link.click();
          URL.revokeObjectURL(url);
          toast.success(t('common.qr_downloaded'));
        }
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <div className="qr-code-display">
      <div className="qr-code-display__header">
        <span className="qr-code-display__label">
          {t('common.wallet_address')} ({network})
        </span>
      </div>
      
      <div className="qr-code-display__qr" ref={qrRef}>
        <QRCodeSVG
          value={address}
          size={200}
          level="H"
          includeMargin={true}
          style={{ background: 'white', padding: '16px', borderRadius: '8px' }}
        />
      </div>

      <div className="qr-code-display__address">
        <code>{address}</code>
      </div>

      <div className="qr-code-display__actions">
        <button
          className="cta-button cta-small cta-outline"
          onClick={handleCopyAddress}
          style={{ flex: 1 }}
        >
          <span>{copied ? t('common.address_copied') : t('common.copy_address')}</span>
        </button>
        <button
          className="cta-button cta-small cta-violet-light"
          onClick={handleDownloadQR}
          style={{ flex: 1 }}
        >
          <span>{t('common.download_qr')}</span>
        </button>
      </div>
    </div>
  );
};

export default QRCodeDisplay;
